import React from 'react';
import { ArraySlot, Slot } from 'react-plugin';
import styled from 'styled-components';
import { useDrag } from '../../hooks/useDrag.js';
import { grey32 } from '../../style/colors.js';
import { quick } from '../../style/vars.js';
import { ControlPanel } from './ControlPanel.js';
import { HomeOverlay } from './HomeOverlay/HomeOverlay.js';
import { NavPanel } from './NavPanel.js';
import { RendererHeader } from './RendererHeader.js';
export function Root({ fixtureItems, selectedFixtureId, rendererConnected, getFixtureState, setFixtureState, navPanelOpen, controlPanelOpen, navPanelWidth, controlPanelWidth, drawerPanels, globalActionOrder, globalOrder, navPanelRowOrder, controlPanelRowOrder, fixtureActionOrder, rendererActionOrder, onToggleNavPanel, onToggleControlPanel, onReloadRenderer, onCloseFixture, setNavPanelWidth, setControlPanelWidth, setDrawerPanels, }) {
    const navDrag = useDrag({
        value: navPanelWidth,
        onChange: setNavPanelWidth,
    });
    const panelDrag = useDrag({
        value: controlPanelWidth,
        reverse: true,
        onChange: setControlPanelWidth,
    });
    const dragging = navDrag.dragging || panelDrag.dragging;
    const showNavPanel = navPanelOpen || !selectedFixtureId;
    // z-indexes are set here on purpose to show the layer hierarchy at a glance
    return (React.createElement(Container, { dragging: dragging },
        (showNavPanel || drawerPanels) && (React.createElement(ResizablePanel, { inert: !showNavPanel, style: {
                position: drawerPanels ? 'absolute' : 'relative',
                left: 0,
                zIndex: 3,
                width: navPanelWidth,
                transform: drawerPanels
                    ? `translateX(${showNavPanel ? 0 : -navPanelWidth}px)`
                    : undefined,
            } },
            React.createElement(NavPanel, { rendererConnected: rendererConnected, drawerPanels: drawerPanels, setDrawerPanels: setDrawerPanels, navPanelRowOrder: navPanelRowOrder, globalActionOrder: globalActionOrder, onClose: onToggleNavPanel }),
            navDrag.dragging && React.createElement(DragOverlay, null),
            React.createElement(NavDragHandle, { ref: navDrag.dragElRef }))),
        React.createElement(MainContainer, { key: "main", style: { zIndex: 1 } },
            React.createElement(RendererContainer, { key: "renderer", style: {
                    filter: drawerPanels
                        ? `brightness(${showNavPanel || controlPanelOpen ? 0.3 : 1})`
                        : undefined,
                } },
                selectedFixtureId && (React.createElement(RendererHeader, { fixtureItems: fixtureItems, fixtureId: selectedFixtureId, navPanelOpen: navPanelOpen, controlPanelOpen: controlPanelOpen, drawerPanels: drawerPanels, fixtureActionOrder: fixtureActionOrder, rendererActionOrder: rendererActionOrder, onToggleNavPanel: onToggleNavPanel, onToggleControlPanel: onToggleControlPanel, onReloadRenderer: onReloadRenderer, onClose: onCloseFixture })),
                React.createElement(RendererBody, { key: "rendererBody" },
                    React.createElement(Slot, { name: "rendererPreview" }))),
            !selectedFixtureId && (React.createElement(Slot, { name: "homeOverlay" },
                React.createElement(HomeOverlay, null))),
            selectedFixtureId &&
                drawerPanels &&
                (navPanelOpen || controlPanelOpen) && (React.createElement(PanelBgOverlay, { onClick: () => {
                    if (navPanelOpen)
                        onToggleNavPanel();
                    if (controlPanelOpen)
                        onToggleControlPanel();
                } })),
            dragging && React.createElement(DragOverlay, null)),
        selectedFixtureId && (controlPanelOpen || drawerPanels) && (React.createElement(ResizablePanel, { inert: !controlPanelOpen, style: {
                position: drawerPanels ? 'absolute' : 'relative',
                right: 0,
                zIndex: 2,
                width: controlPanelWidth,
                transform: drawerPanels
                    ? `translateX(${controlPanelOpen ? 0 : controlPanelWidth}px)`
                    : undefined,
            } },
            React.createElement(ControlPanel, { fixtureId: selectedFixtureId, getFixtureState: getFixtureState, setFixtureState: setFixtureState, rowOrder: controlPanelRowOrder, onClose: onToggleControlPanel }),
            panelDrag.dragging && React.createElement(DragOverlay, null),
            React.createElement(PanelDragHandle, { ref: panelDrag.dragElRef }))),
        React.createElement("div", { style: { zIndex: 4 } },
            React.createElement(ArraySlot, { name: "global", plugOrder: globalOrder }))));
}
const Container = styled.div.attrs({ 'data-testid': 'root' }) `
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  display: flex;
  background: ${grey32};
  cursor: ${props => (props.dragging ? 'ew-resize' : 'default')};
`;
const ResizablePanel = styled.div `
  flex-shrink: 0;
  max-width: 100%;
  height: 100%;
  transition: transform ${quick}s;
`;
const MainContainer = styled.div `
  flex: 1;
  position: relative;
`;
const RendererContainer = styled.div `
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: filter ${quick}s;
`;
const RendererBody = styled.div `
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;
const DragHandle = styled.div `
  position: absolute;
  top: 0;
  width: 10px;
  height: 100%;
  background-clip: content-box;
  cursor: ew-resize;
  user-select: none;
  touch-action: none;
`;
const NavDragHandle = styled(DragHandle) `
  right: -2px;
`;
const PanelDragHandle = styled(DragHandle) `
  left: -2px;
`;
// The purpose of DragOverlay is to cover the renderer iframe while dragging,
// because otherwise the iframe steals the mousemove events and stops the drag.
const DragOverlay = styled.div `
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;
const PanelBgOverlay = styled.div `
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;
